import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { ParentalConsentModal } from "@/components/ParentalConsentModal";
import { GuardAlert } from "@/components/GuardAlert";
import AuthPage from "@/pages/AuthPage";
import Feed from "@/pages/Feed";
import LiveRoom from "@/pages/LiveRoom";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import SuperAdmin from "@/pages/SuperAdmin";
import ShortsFeed from "@/pages/ShortsFeed";
import CreatePost from "@/pages/CreatePost";
import Profile from "@/pages/Profile";
import Onboarding from "@/pages/Onboarding";
import Messages from "@/pages/Messages";
import Search from "@/pages/Search";

function AuthCallback() {
  const { loginWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    loginWithGoogle(sid)
      .then(() => navigate("/", { replace: true }))
      .catch((e) => { toast.error(formatApiError(e)); navigate("/entrar", { replace: true }); });
  }, [location.hash, loginWithGoogle, navigate]);
  return <div className="grid min-h-screen place-items-center text-muted-foreground" data-testid="google-callback">A entrar com o Google…</div>;
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="auth-loading">
        Carregando…
      </div>
    );
  if (!user) return <Navigate to="/entrar" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (user.onboarding_required) return <Onboarding />;
  return (
    <>
      {children}
      {user.is_minor && !user.parental_consent && <ParentalConsentModal />}
    </>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/entrar" element={<PublicOnly><AuthPage /></PublicOnly>} />
            <Route path="/sala/:id" element={<Protected><LiveRoom /></Protected>} />
            <Route path="/" element={<Protected><ShortsFeed /></Protected>} />
            <Route path="/curtas" element={<Protected><ShortsFeed /></Protected>} />
            <Route path="/curtas/:id" element={<Protected><ShortsFeed key="deep" /></Protected>} />
            <Route path="/gravar" element={<Protected><CreatePost /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/lives" element={<Feed />} />
              <Route path="/perfil/:userId" element={<Profile />} />
              <Route path="/mensagens" element={<Messages />} />
              <Route path="/mensagens/:convId" element={<Messages />} />
              <Route path="/buscar" element={<Search />} />
              <Route path="/painel" element={<Protected roles={["institution", "admin"]}><Dashboard /></Protected>} />
              <Route path="/admin" element={<Navigate to="/super-admin" replace />} />
              <Route path="/super-admin" element={<Protected roles={["admin"]}><SuperAdmin /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <GuardAlert />
      </AuthProvider>
    </div>
  );
}

export default App;
